import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { MonoLabel } from "@/components/proof/primitives";
import { easing } from "@/lib/animations";
import { DROPS } from "@/data/drops";
import { PRODUCTS } from "@/data/products";

const yearOf = (iso) => new Date(iso).getFullYear();

const ArchiveCard = ({ drop, index }) => {
    const products = PRODUCTS.filter((p) => p.drop === drop.productDropTag);
    return (
        <motion.article
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.18 }}
            transition={{
                duration: 0.9,
                delay: 0.08 + index * 0.06,
                ease: easing.primary,
            }}
            data-testid={`archive-card-${drop.slug}`}
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
                gap: "clamp(28px, 4vw, 60px)",
                paddingBottom: "clamp(36px, 4vw, 56px)",
                borderBottom: "1px solid var(--on-ink-line)",
                alignItems: "start",
            }}
            className="proof-grid-pdp"
        >
            <Link
                to={`/drop/${drop.slug}`}
                style={{
                    color: "inherit",
                    textDecoration: "none",
                    display: "block",
                }}
            >
                <div
                    style={{
                        aspectRatio: "4 / 5",
                        background: drop.bgTone || "#1a1a1a",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <span
                        style={{
                            position: "absolute",
                            top: 14,
                            left: 14,
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "rgba(12,12,12,0.55)",
                        }}
                    >
                        [{drop.n}] · {yearOf(drop.launchAt)}
                    </span>
                    <img
                        src={drop.heroPoster}
                        alt={drop.title}
                        draggable={false}
                        style={{
                            width: "65%",
                            height: "75%",
                            objectFit: "contain",
                            mixBlendMode: "multiply",
                        }}
                    />
                </div>
            </Link>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                <MonoLabel tone="mute">
                    Drop [{drop.n}] · {yearOf(drop.launchAt)} · Closed
                </MonoLabel>
                <Link
                    to={`/drop/${drop.slug}`}
                    style={{
                        color: "var(--on-ink)",
                        textDecoration: "none",
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(36px, 5vw, 80px)",
                        letterSpacing: "-0.035em",
                        lineHeight: 0.95,
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    {drop.title}
                </Link>
                <p
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: "clamp(16px, 1.3vw, 21px)",
                        lineHeight: 1.5,
                        color: "var(--on-ink-soft)",
                        maxWidth: "44ch",
                    }}
                >
                    {drop.tagline}
                </p>

                <dl
                    style={{
                        margin: "8px 0 0",
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: "10px 18px",
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: "var(--on-ink-mute)",
                    }}
                >
                    <dt>Released</dt>
                    <dd style={{ margin: 0, color: "var(--on-ink-soft)" }}>
                        {new Date(drop.launchAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                        })}
                    </dd>
                    <dt>Artifacts</dt>
                    <dd style={{ margin: 0, color: "var(--on-ink-soft)" }}>
                        {String(products.length).padStart(2, "0")} in rotation
                    </dd>
                    <dt>Status</dt>
                    <dd style={{ margin: 0, color: "var(--signal)" }}>
                        Closed · Past field
                    </dd>
                </dl>

                <Link
                    to={`/drop/${drop.slug}`}
                    data-testid={`archive-cta-${drop.slug}`}
                    style={{
                        marginTop: 14,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        color: "var(--on-ink)",
                        textDecoration: "none",
                        border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.45))",
                        padding: "12px 22px",
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        alignSelf: "flex-start",
                    }}
                >
                    View Field
                    <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                        <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="currentColor" />
                        <path d="M16 1L20 4.5L16 8" stroke="currentColor" fill="none" />
                    </svg>
                </Link>
            </div>
        </motion.article>
    );
};

export default function Archive() {
    // Past drops: anything with a launchAt in the past
    const now = Date.now();
    const archived = DROPS.filter((d) => new Date(d.launchAt).getTime() < now);

    return (
        <PageShell
            sectionNumber="15"
            eyebrow="Past fields · Sold out"
            title="Archive"
            crumb="Archive"
            data-testid="page-archive"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(40px, 5vw, 80px)",
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: "clamp(16px, 1.3vw, 21px)",
                        lineHeight: 1.55,
                        color: "var(--on-ink-soft)",
                        maxWidth: "60ch",
                    }}
                    data-testid="archive-intro"
                >
                    Every Field is finite. Once closed, a drop does not
                    restock. The Archive is a record of what the atelier
                    released, when — and what it taught the next collection.
                </p>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingBottom: 14,
                        borderBottom: "1px solid var(--on-ink-line)",
                    }}
                >
                    <MonoLabel>
                        {String(archived.length).padStart(2, "0")} · Fields closed
                    </MonoLabel>
                    <MonoLabel tone="mute">2024 — present</MonoLabel>
                </div>

                {archived.length === 0 ? (
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-serif)",
                            fontStyle: "italic",
                            fontSize: 19,
                            color: "var(--on-ink-mute)",
                            maxWidth: "44ch",
                        }}
                        data-testid="archive-empty"
                    >
                        The archive is still being assembled. The first Field
                        is open. Visit again after the next launch.
                    </p>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "clamp(36px, 4vw, 60px)",
                        }}
                        data-testid="archive-list"
                    >
                        {archived.map((d, i) => (
                            <ArchiveCard key={d.slug} drop={d} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </PageShell>
    );
}
